import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogConfig as MatDialogConfig} from "@angular/material/legacy-dialog";
import { MatStepper } from "@angular/material/stepper";
import { MatLegacyAutocomplete as MatAutocomplete} from "@angular/material/legacy-autocomplete";
import { Guid } from "guid-typescript";
import * as _ from "lodash";
import * as moment from "moment";
import { FileValidator, FileInputComponent } from "ngx-material-file-input";
import { BehaviorSubject, Observable, forkJoin, Subscription } from "rxjs";
import { first, map, startWith, take, debounceTime, switchMap } from "rxjs/operators";

import { AppGlobals } from "../../app.globals";
import { ICountry, IProvider, IUser } from "../../_models";
import { AdmissionsService, AlertService, LookupsService, StorageService, UserService, GoogleAnalyticsService, MediaWatcherService } from "../../_services";
import { UnSavedChangesDialogComponent } from "../../_components/unSavedChangesDialog/unSavedChangesDialog.component";
import { dateGreaterThenValidator } from "../../_helpers/forms/date.validator";

@Component({
    templateUrl: "admissionsApplicationDialog.component.html",
    styleUrls: ["admissionsApplicationDialog.component.css"]
})
export class AdmissionsApplicationDialogComponent implements OnInit, OnDestroy {
    personalDetailsFormGroup: UntypedFormGroup;
    educationFormGroup: UntypedFormGroup;
    familyFormGroup: UntypedFormGroup;
    reviewFormGroup: UntypedFormGroup;
    isEditable = true;
    currentUser: IUser;
    applicationTypes = [];
    countries: ICountry[];
    primaryCitizenships: null;
    secondaryCitizenships: null;
    languages: null;
    states = [];
    filteredStates = [];
    filteredBirthStates = [];
    filteredEmergencyContact1States = [];
    filteredEmergencyContact2States = [];
    //applicationSteps: null;
    genders: null;
    programs = [];
    scholasticPeriods = [];
    allScholasticPeriods = [];
    filteredScholasticPeriods = [];
    applicationSources: null;
    studyAreas = [];
    applicationId = 0;
    applicationRowversion: string;
    currentScholasticPeriod: any;
    filteredStudyAreas: null;
    selectedStepIndex: null;
    allowEdit: boolean;
    existingApplicationScholasticPeriods: number[];
    isCancelled = false;
    admissionsApplicationStepId = 0;
    isMobile = false;
    mediaWatcherSubscription: Subscription;
    selectedApplicationType: null;
    studentCode = null;
    existingEmergencyContactInformationFound = false;

    //education form
    educationDetails = new BehaviorSubject<any[]>([]);
    educationDetails$: Observable<any> = this.educationDetails.asObservable();

    displayedColumns = [];

    providers: IProvider[] = [];
    filteredProviders: Observable<IProvider[]>;
    fileToUpload: File = null;
    // 1 MB
    readonly maxSize = 1048576;

    educationDetailsRowversion: string;
    educationDetailsId = 0;
    essayFileName: string = null;
    essayFileType: string = null;
    essayAttachmentId: number = null;

    educationInformationChanged = false;

    @ViewChild("essayAttachment") essayAttachmentInput: FileInputComponent;
    @ViewChild("stepper", { static: true }) stepper: MatStepper;
    @ViewChild("auto") matAutocomplete: MatAutocomplete;

    //family form
    educationLevels: null;
    contactTypes: null;

    primaryContactRowversion: string;
    secondaryContactRowversion: string;
    primaryContactId: number = 0;
    secondaryContactId: number = 0;

    constructor(private dialogRef: MatDialogRef<AdmissionsApplicationDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: any,
        private formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        private appGlobals: AppGlobals,
        private storageService: StorageService,
        private alertService: AlertService,
        private lookupsService: LookupsService,
        private admissionsService: AdmissionsService,
        private userService: UserService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private mediaWatcherService: MediaWatcherService) {
        this.currentUser = data.currentUser;
        this.applicationId = data.applicationId;
        this.selectedStepIndex = data.selectedStepIndex;
        this.allowEdit = data.allowEdit;
        this.existingApplicationScholasticPeriods = data.existingApplicationScholasticPeriods;
    }

    ngOnDestroy() {
        this.educationDetails.unsubscribe();
        this.mediaWatcherSubscription.unsubscribe();
    }

    ngOnInit() {
        //this.googleAnalyticsService.logPageView("/admissionsApplication");

        this.stepper.selectedIndex = this.selectedStepIndex;

        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;

            if (this.isMobile)
                this.displayedColumns = ["schoolDescription", "city", "countryDescription", "stateDescription", "enrollmentDateFrom", "enrollmentDateTo", "isResyncEnabled", "delete"];
            else
                this.displayedColumns = ["id", "schoolDescription", "city", "countryDescription", "stateDescription", "enrollmentDateFrom", "enrollmentDateTo", "isResyncEnabled", "delete"];
        });

        //application types
        this.lookupsService.getAdmissionsApplicationTypes()
            .pipe(first())
            .subscribe((response: any) => {
                this.applicationTypes = response;
            });

        //countries
        this.lookupsService.getCountries()
            .pipe(first())
            .subscribe((response: any) => {
                this.countries = _.orderBy(response, "description") as any;
                this.primaryCitizenships = _.orderBy(response, "description") as any;
                this.secondaryCitizenships = _.orderBy(response, "description") as any;
            });

        //languages
        this.lookupsService.getLanguages()
            .pipe(first())
            .subscribe((response: any) => {
                this.languages = _.orderBy(response.filter(f => f.isActive), "description") as any;;
            });

        //states
        this.lookupsService.getStates()
            .pipe(first())
            .subscribe((response: any) => {
                this.states = _.orderBy(response, "description") as any;;
            });

        //genderTypes
        this.lookupsService.getGenderTypes()
            .pipe(first())
            .subscribe((response: any) => {
                this.genders = _.filter(response, f => f.description !== "Both") as any;
            });

        //study areas
        this.lookupsService.getAdmissionsApplicationStudyAreas()
            .pipe(first())
            .subscribe((response: any) => {
                this.studyAreas = _.orderBy(_.filter(response, (f: any) => { return f.isActive; }), "description") as any;
            });

        //application sources
        this.lookupsService.getAdmissionsApplicationSources()
            .pipe(first())
            .subscribe((response: any) => {
                this.applicationSources =
                    _.orderBy(_.filter(response, (f: any) => { return f.isActive && f.isVisibleInWeb; }),
                        "description") as any;
            });

        //application education levels
        this.lookupsService.getEducationLevels()
            .pipe(first())
            .subscribe((response: any) => {
                this.educationLevels = _.orderBy(response, "description") as any;
            });

        //emergency contact types
        this.lookupsService.getStudentEmergencyContactTypes()
            .pipe(first())
            .subscribe((response: any) => {
                this.contactTypes = _.orderBy(response, "description") as any;
            });

        this.initializeForm();

        this.onChanges();

        const semesters = this.lookupsService.getSemesters();
        const programs = this.lookupsService.getPrograms();
        const applicationDetails = this.applicationId !== 0 ? this.admissionsService.getAdmissionsApplication(this.applicationId) : this.userService.getStudentDetails(this.currentUser.id);

        forkJoin([semesters, programs, applicationDetails])
            .subscribe(([semesters, programs, applicationDetails]) => {
                _.forEach(semesters, (f: any) => {
                    const scholasticPeriod = {
                        id: this.allScholasticPeriods.length + 1,
                        scholasticPeriodId: f.id,
                        programId: null,
                        startDate: f.startDate,
                        endDate: f.endDate,
                        title: f.title,
                        schoolId: f.schoolId,
                        isActive: f.isActive,
                        isOpenForAdmissions: f.isOpenForAdmissions
                    };

                    this.allScholasticPeriods.push(scholasticPeriod);
                });

                this.programs = programs as any[];

                _.forEach(programs, (f: any) => {
                    const scholasticPeriod = {
                        id: this.allScholasticPeriods.length + 1,
                        scholasticPeriodId: null,
                        programScholasticPeriodId: f.scholasticPeriodId,
                        programId: f.id,
                        startDate: f.startDate,
                        endDate: f.endDate,
                        title: f.description,
                        schoolId: f.schoolId,
                        isActive: f.isActive,
                        isOpenForAdmissions: f.isOpenForAdmissions
                    };

                    this.allScholasticPeriods.push(scholasticPeriod);
                });

                this.scholasticPeriods = _.orderBy(this.allScholasticPeriods.filter((f: any) => f.isOpenForAdmissions && f.isActive), ["startDate", "schoolId", "programId"], ["desc", "asc", "desc"]);

                var self = this;

                if (applicationDetails) {
                    var result = applicationDetails as any;
                    this.studentCode = result.studentCode;

                    if (this.applicationId !== 0) {
                        this.applicationRowversion = result.rowversion;
                        this.admissionsApplicationStepId = result.admissionsApplicationStepId;
                        this.isCancelled = result.isCancelled;

                        const scholasticPeriod = this.allScholasticPeriods.find(f => result.programId ? f.programId === result.programId : f.scholasticPeriodId === result.scholasticPeriodId);
                        const additionalScholasticPeriod = result.additionalScholasticPeriodId ? this.allScholasticPeriods.find(f => result.additionalProgramId ? f.programId === result.additionalProgramId : f.scholasticPeriodId === result.additionalScholasticPeriodId) : null;

                        this.personalDetailsFormGroup.get("admissionsApplicationTypeId").setValue(result.admissionsApplicationTypeId);
                        this.personalDetailsFormGroup.get("waiverCode").setValue(result.waiverCode);
                        this.personalDetailsFormGroup.get("scholasticPeriodId").setValue(scholasticPeriod.id);
                        this.personalDetailsFormGroup.get("additionalScholasticPeriodId").setValue(additionalScholasticPeriod ? additionalScholasticPeriod.id : null);
                        this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").setValue(result.admissionsApplicationStudyAreaId);
                        this.personalDetailsFormGroup.get("admissionsApplicationSourceId").setValue(result.admissionsApplicationSourceId);
                        this.personalDetailsFormGroup.get("middleName").setValue(result.admissionsApplicationPersonalInformation.middleName);
                        this.personalDetailsFormGroup.get("email").setValue(result.admissionsApplicationPersonalInformation.email);
                        this.personalDetailsFormGroup.get("genderTypeId").setValue(result.admissionsApplicationPersonalInformation.genderTypeId);
                        this.personalDetailsFormGroup.get("dateOfBirth").setValue(result.admissionsApplicationPersonalInformation.dateOfBirth);
                        this.personalDetailsFormGroup.get("birthCity").setValue(result.admissionsApplicationPersonalInformation.birthCity);
                        this.personalDetailsFormGroup.get("birthCountryId").setValue(result.admissionsApplicationPersonalInformation.birthCountryId);
                        this.personalDetailsFormGroup.get("birthStateId").setValue(result.admissionsApplicationPersonalInformation.birthStateId);
                        this.personalDetailsFormGroup.get("languageId").setValue(result.admissionsApplicationPersonalInformation.languageId);
                        this.personalDetailsFormGroup.get("primaryCitizenshipId").setValue(result.admissionsApplicationPersonalInformation.primaryCitizenshipId);
                        this.personalDetailsFormGroup.get("secondaryCitizenshipId").setValue(result.admissionsApplicationPersonalInformation.secondaryCitizenshipId);
                        this.personalDetailsFormGroup.get("address").setValue(result.admissionsApplicationPersonalInformation.address);
                        this.personalDetailsFormGroup.get("city").setValue(result.admissionsApplicationPersonalInformation.city);
                        this.personalDetailsFormGroup.get("postCode").setValue(result.admissionsApplicationPersonalInformation.postCode);
                        this.personalDetailsFormGroup.get("phoneNo").setValue(result.admissionsApplicationPersonalInformation.phoneNo);
                        this.personalDetailsFormGroup.get("countryId").setValue(result.admissionsApplicationPersonalInformation.countryId);
                        this.personalDetailsFormGroup.get("stateId").setValue(result.admissionsApplicationPersonalInformation.stateId);

                        if (result.dateCompleted)
                            this.reviewFormGroup.get("isHousingRequired").setValue(result.isHousingRequired ? 1 : 0);

                        //education information
                        if (result.admissionsApplicationEducationDetail) {
                            this.educationDetailsRowversion = result.admissionsApplicationEducationDetail.rowversion;
                            this.educationDetailsId = result.admissionsApplicationEducationDetail.id;
                            this.essayFileName = result.admissionsApplicationEducationDetail.fileName;
                            this.essayFileType = result.admissionsApplicationEducationDetail.fileType;
                            this.essayAttachmentId = result.admissionsApplicationEducationDetail.essayAttachmentId;

                            this.educationFormGroup.get("gapYearDetails").setValue(result.admissionsApplicationEducationDetail.gapYearDetails);
                            this.educationFormGroup.get("misconductInfo").setValue(result.admissionsApplicationEducationDetail.misconductInfo);

                            var educationDetails = [];

                            _.forEach(result.admissionsApplicationEducationDetail
                                .admissionsApplicationEducationInformations,
                                (f: any) => {
                                    const country = _.find(self.countries, { "id": f.countryId }) as ICountry;
                                    const state = _.find(self.states, { "id": f.stateId }) as any;

                                    //const provider = _.find(self.providers, { "organizationBbUniqueId": f.organizationBbUniqueId }) as IProvider;

                                    const educationData = {
                                        id: f.id,
                                        organizationBbUniqueId: f.organizationBbUniqueId,
                                        schoolDescription: f.school ? f.school : f.organizationName,
                                        countryId: f.countryId,
                                        countryDescription: country ? country.description : null,
                                        stateId: f.stateId,
                                        stateDescription: state ? state.description : null,
                                        city: f.city,
                                        enrollmentDateFrom: f.enrollmentDateFrom ? moment(f.enrollmentDateFrom).format("YYYY-MM-DD") : null,
                                        enrollmentDateTo: f.enrollmentDateTo ? moment(f.enrollmentDateTo).format("YYYY-MM-DD") : null,
                                        isResyncEnabled: f.isResyncEnabled
                                    };

                                    educationDetails.push(educationData);
                                });

                            this.educationDetails.next(_.orderBy(educationDetails, ["enrollmentDateFrom"], ["desc"]));

                            if (result.admissionsApplicationEducationDetail.fileName) {
                                this.educationFormGroup.get("essayAttachment").setValidators([FileValidator.maxContentSize(this.maxSize)]);
                                this.educationFormGroup.get("essayAttachment").updateValueAndValidity();
                            }
                        }

                        //family information
                        if (result.admissionsApplicationFamilyInformations && result.admissionsApplicationFamilyInformations.length > 0) {
                            const primaryContact = _.find(result.admissionsApplicationFamilyInformations, ["isPrimaryContact", true]) as any;
                            const secondaryContact = _.find(result.admissionsApplicationFamilyInformations, ["isPrimaryContact", false]) as any;

                            this.populateEmergencyContacts(primaryContact, secondaryContact);
                            this.existingEmergencyContactInformationFound = this.allowEdit;

                            //if continuing an application and getting existing emergency contact information for the student
                            if (this.selectedStepIndex === 2)
                                this.familyFormGroup.markAsDirty();
                        }
                        //}
                    } else {
                        this.personalDetailsFormGroup.get("middleName").setValue(result.middleName);
                        this.personalDetailsFormGroup.get("email").setValue(result.primaryEmailAddress);
                        this.personalDetailsFormGroup.get("genderTypeId").setValue(result.genderTypeId);
                        this.personalDetailsFormGroup.get("dateOfBirth").setValue(result.dateOfBirth);
                        this.personalDetailsFormGroup.get("birthCity").setValue(result.birthCity);
                        this.personalDetailsFormGroup.get("birthCountryId").setValue(result.birthCountryId);
                        this.personalDetailsFormGroup.get("birthStateId").setValue(result.birthStateId);
                        this.personalDetailsFormGroup.get("languageId").setValue(result.languageId);
                        this.personalDetailsFormGroup.get("primaryCitizenshipId").setValue(result.primaryCitizenshipId);
                        this.personalDetailsFormGroup.get("secondaryCitizenshipId").setValue(result.secondaryCitizenshipId);
                        this.personalDetailsFormGroup.get("address").setValue(result.address);
                        this.personalDetailsFormGroup.get("city").setValue(result.city);
                        this.personalDetailsFormGroup.get("postCode").setValue(result.postCode);
                        this.personalDetailsFormGroup.get("phoneNo").setValue(result.defaultPhoneNumber);
                        this.personalDetailsFormGroup.get("countryId").setValue(result.countryId);
                        this.personalDetailsFormGroup.get("stateId").setValue(result.stateId);

                        //family information
                        if (result.studentEmergencyContacts && result.studentEmergencyContacts.length > 0) {
                            const primaryContact = _.find(result.studentEmergencyContacts, ["isPrimaryContact", true]) as any;

                            if (primaryContact)
                                primaryContact.id = 0;

                            const secondaryContact = _.find(result.studentEmergencyContacts, ["isPrimaryContact", false]) as any;

                            if (secondaryContact)
                                secondaryContact.id = 0;

                            this.populateEmergencyContacts(primaryContact, secondaryContact);

                            this.familyFormGroup.markAsDirty();
                        }
                    }
                }
            });
    }

    populateEmergencyContacts(primaryContact: any, secondaryContact: any) {
        if (primaryContact) {
            this.primaryContactRowversion = primaryContact.rowversion;
            this.primaryContactId = primaryContact.id;

            this.familyFormGroup.get("firstName").setValue(primaryContact.firstName);
            this.familyFormGroup.get("lastName").setValue(primaryContact.lastName);
            this.familyFormGroup.get("studentEmergencyContactTypeId").setValue(primaryContact.studentEmergencyContactTypeId);
            this.familyFormGroup.get("genderTypeId").setValue(primaryContact.genderTypeId);
            this.familyFormGroup.get("birthCountryId").setValue(primaryContact.birthCountryId);
            this.familyFormGroup.get("address").setValue(primaryContact.address);
            this.familyFormGroup.get("city").setValue(primaryContact.city);
            this.familyFormGroup.get("postCode").setValue(primaryContact.postCode);
            this.familyFormGroup.get("phoneNo").setValue(primaryContact.phoneNo);
            this.familyFormGroup.get("countryId").setValue(primaryContact.countryId);
            this.familyFormGroup.get("stateId").setValue(primaryContact.stateId);
            this.familyFormGroup.get("email").setValue(primaryContact.email);
            this.familyFormGroup.get("educationLevelId").setValue(primaryContact.educationLevelId);
            this.familyFormGroup.get("occupation").setValue(primaryContact.occupation);
            this.familyFormGroup.get("employer").setValue(primaryContact.employer);
            this.familyFormGroup.get("isEmergencyContact").setValue(primaryContact.isEmergencyContact);
        }

        if (secondaryContact) {
            this.secondaryContactRowversion = secondaryContact.rowversion;
            this.secondaryContactId = secondaryContact.id;

            this.familyFormGroup.get("firstName2").setValue(secondaryContact.firstName);
            this.familyFormGroup.get("lastName2").setValue(secondaryContact.lastName);
            this.familyFormGroup.get("studentEmergencyContactTypeId2").setValue(secondaryContact.studentEmergencyContactTypeId);
            this.familyFormGroup.get("genderTypeId2").setValue(secondaryContact.genderTypeId);
            this.familyFormGroup.get("birthCountryId2").setValue(secondaryContact.birthCountryId);
            this.familyFormGroup.get("address2").setValue(secondaryContact.address);
            this.familyFormGroup.get("city2").setValue(secondaryContact.city);
            this.familyFormGroup.get("postCode2").setValue(secondaryContact.postCode);
            this.familyFormGroup.get("phoneNo2").setValue(secondaryContact.phoneNo);
            this.familyFormGroup.get("countryId2").setValue(secondaryContact.countryId);
            this.familyFormGroup.get("stateId2").setValue(secondaryContact.stateId);
            this.familyFormGroup.get("email2").setValue(secondaryContact.email);
            this.familyFormGroup.get("educationLevelId2").setValue(secondaryContact.educationLevelId);
            this.familyFormGroup.get("occupation2").setValue(secondaryContact.occupation);
            this.familyFormGroup.get("employer2").setValue(secondaryContact.employer);
            this.familyFormGroup.get("isEmergencyContact2").setValue(secondaryContact.isEmergencyContact);
        }
    }

    initializeForm() {
        this.personalDetailsFormGroup = this.formBuilder.group({
            admissionsApplicationTypeId: [{ value: "", disabled: !this.allowEdit }, Validators.required],
            waiverCode: [{ value: "", disabled: !this.allowEdit }],
            scholasticPeriodId: [{ value: "", disabled: true }, Validators.required],
            additionalScholasticPeriodId: [{ value: "", disabled: true }],
            admissionsApplicationStudyAreaId: [{ value: "", disabled: true }, Validators.required],
            admissionsApplicationSourceId: [{ value: "", disabled: true }, Validators.required],
            firstName: [{ value: this.currentUser.firstName, disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            middleName: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]], //no special chars regex
            lastName: [{ value: this.currentUser.lastName, disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            genderTypeId: [{ value: "", disabled: true }, Validators.required],
            dateOfBirth: [{ value: "", disabled: true }, Validators.required],
            birthCity: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            birthCountryId: [{ value: "", disabled: true }, Validators.required],
            birthStateId: [{ value: "", disabled: true }],
            languageId: [{ value: "", disabled: true }, Validators.required],
            primaryCitizenshipId: [{ value: "", disabled: true }, Validators.required],
            secondaryCitizenshipId: [{ value: "", disabled: true }],
            address: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            city: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            postCode: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            countryId: [{ value: "", disabled: true }, Validators.required],
            stateId: [{ value: "", disabled: true }],
            email: [{ value: this.currentUser.email, disabled: true }, [Validators.required, Validators.email]],
            phoneNo: [{ value: "", disabled: true }, [Validators.required, Validators.minLength(10), Validators.pattern(/^[+]{1}\d+$/)]]
        });

        this.educationFormGroup = this.formBuilder.group({
            organizationBbUniqueId: [{ value: "", disabled: !this.allowEdit }],
            city: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            countryId: [{ value: "", disabled: !this.allowEdit }],
            stateId: [{ value: "", disabled: !this.allowEdit }],
            enrollmentDateFrom: [{ value: "", disabled: !this.allowEdit }],
            enrollmentDateTo: [{ value: "", disabled: !this.allowEdit }, dateGreaterThenValidator("enrollmentDateFrom", false)],
            gapYearDetails: [{ value: "", disabled: !this.allowEdit }],
            misconductInfo: [{ value: "", disabled: !this.allowEdit }],
            essayAttachment: [{ value: "", disabled: !this.allowEdit }, [Validators.required, FileValidator.maxContentSize(this.maxSize)]]
        });

        this.familyFormGroup = this.formBuilder.group({
            firstName: [{ value: "", disabled: !this.allowEdit }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            lastName: [{ value: "", disabled: !this.allowEdit }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            genderTypeId: [{ value: "", disabled: !this.allowEdit }, Validators.required],
            studentEmergencyContactTypeId: [{ value: "", disabled: !this.allowEdit }, Validators.required],
            birthCountryId: [{ value: "", disabled: !this.allowEdit }],
            address: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            city: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            postCode: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            countryId: [{ value: "", disabled: !this.allowEdit }],
            stateId: [{ value: "", disabled: !this.allowEdit }],
            email: [{ value: "", disabled: !this.allowEdit }, [Validators.email, Validators.required]],
            phoneNo: [
                { value: "", disabled: !this.allowEdit },
                [Validators.required, Validators.minLength(10), Validators.pattern(/^[+]{1}\d+$/)]
            ],
            educationLevelId: [{ value: "", disabled: !this.allowEdit }],
            occupation: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            employer: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            isEmergencyContact: [{ value: true, disabled: true }],
            firstName2: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            lastName2: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            genderTypeId2: [{ value: "", disabled: true }, Validators.required],
            studentEmergencyContactTypeId2: [{ value: "", disabled: true }, Validators.required],
            birthCountryId2: [{ value: "", disabled: true }],
            address2: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            city2: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            postCode2: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            countryId2: [{ value: "", disabled: true }],
            stateId2: [{ value: "", disabled: true }],
            email2: [{ value: "", disabled: true }, [Validators.email, Validators.required]],
            phoneNo2: [
                { value: "", disabled: true },
                [Validators.minLength(10), Validators.pattern(/^[+]{1}\d+$/), Validators.required]
            ],
            educationLevelId2: [{ value: "", disabled: true }],
            occupation2: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            employer2: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            isEmergencyContact2: [{ value: "", disabled: true }]
        });

        this.reviewFormGroup = this.formBuilder.group({
            admissionsApplicationTypeId: [{ value: "", disabled: true }],
            scholasticPeriodId: [{ value: "", disabled: true }],
            additionalScholasticPeriodId: [{ value: "", disabled: true }],
            admissionsApplicationStudyAreaId: [{ value: "", disabled: true }],
            admissionsApplicationSourceId: [{ value: "", disabled: true }],
            genderTypeId: [{ value: "", disabled: true }],
            birthCountryId: [{ value: "", disabled: true }],
            birthStateId: [{ value: "", disabled: true }],
            languageId: [{ value: "", disabled: true }],
            primaryCitizenshipId: [{ value: "", disabled: true }],
            secondaryCitizenshipId: [{ value: "", disabled: true }],
            countryId: [{ value: "", disabled: true }],
            stateId: [{ value: "", disabled: true }],
            primaryEmergencyContactGenderTypeId: [{ value: "", disabled: true }],
            primaryEmergencyContactBirthCountryId: [{ value: "", disabled: true }],
            primaryEmergencyContactCountryId: [{ value: "", disabled: true }],
            primaryEmergencyContactStateId: [{ value: "", disabled: true }],
            educationLevelId: [{ value: "", disabled: true }],
            studentEmergencyContactTypeId: [{ value: "", disabled: true }],
            secondaryEmergencyContactGenderTypeId: [{ value: "", disabled: true }],
            secondaryEmergencyContactBirthCountryId: [{ value: "", disabled: true }],
            secondaryEmergencyContactCountryId: [{ value: "", disabled: true }],
            secondaryEmergencyContactStateId: [{ value: "", disabled: true }],
            educationLevelId2: [{ value: "", disabled: true }],
            studentEmergencyContactTypeId2: [{ value: "", disabled: true }],
            isHousingRequired: [{ value: "", disabled: !this.allowEdit }, Validators.required],
            consent: [{ value: (this.allowEdit ? "" : true), disabled: !this.allowEdit }, Validators.requiredTrue],
            confirmation: [{ value: (this.allowEdit ? "" : true), disabled: !this.allowEdit }, Validators.requiredTrue]
        });
    }

    onChanges() {
        this.personalDetailsFormGroup.get("countryId").valueChanges
            .subscribe(selectedCountry => {
                this.reviewFormControls.countryId.setValue(selectedCountry);

                var phoneCountry = _.find(this.countries, { "id": selectedCountry }) as ICountry;
                var currentPhoneDialingCode = _.find(this.countries, { "dialingCode": this.personalDetailsFormControls.phoneNo.value }) as ICountry;

                if (phoneCountry && (!this.personalDetailsFormControls.phoneNo.value || currentPhoneDialingCode)) {
                    setTimeout(() => {
                        this.personalDetailsFormGroup.get("phoneNo").setValue(phoneCountry.dialingCode);
                    }, null);
                }

                this.filteredStates = this.states.filter((f: any) => f.countryId === selectedCountry);

                if (this.allowEdit && (selectedCountry === 1 || selectedCountry === 11) && this.personalDetailsFormControls.admissionsApplicationTypeId.value) {
                    this.personalDetailsFormGroup.get("stateId").enable();
                } else {
                    this.personalDetailsFormGroup.get("stateId").reset();
                    this.personalDetailsFormGroup.get("stateId").disable();
                }
            });

        this.personalDetailsFormGroup.get("birthCountryId").valueChanges
            .subscribe(selectedCountry => {
                this.reviewFormControls.birthCountryId.setValue(selectedCountry);

                this.filteredBirthStates = this.states.filter((f: any) => f.countryId === selectedCountry);

                if (this.allowEdit && (selectedCountry === 1 || selectedCountry === 11) && this.personalDetailsFormControls.admissionsApplicationTypeId.value) {
                    this.personalDetailsFormGroup.get("birthStateId").enable();
                } else {
                    this.personalDetailsFormGroup.get("birthStateId").reset();
                    this.personalDetailsFormGroup.get("birthStateId").disable();
                }
            });

        this.personalDetailsFormGroup.get("admissionsApplicationTypeId").valueChanges
            .subscribe(selectedApplicationType => {
                this.reviewFormControls.admissionsApplicationTypeId.setValue(selectedApplicationType);

                if (this.allowEdit) {
                    this.personalDetailsFormGroup.get("waiverCode").enable();
                    this.personalDetailsFormGroup.get("scholasticPeriodId").enable();
                    this.personalDetailsFormGroup.get("admissionsApplicationSourceId").enable();

                    if (!this.studentCode) {
                        this.personalDetailsFormGroup.get("firstName").enable();
                        this.personalDetailsFormGroup.get("middleName").enable();
                        this.personalDetailsFormGroup.get("lastName").enable();
                        this.personalDetailsFormGroup.get("dateOfBirth").enable();
                    }

                    this.personalDetailsFormGroup.get("email").enable();
                    this.personalDetailsFormGroup.get("genderTypeId").enable();
                    this.personalDetailsFormGroup.get("birthCity").enable();
                    this.personalDetailsFormGroup.get("birthCountryId").enable();
                    this.personalDetailsFormGroup.get("languageId").enable();
                    this.personalDetailsFormGroup.get("primaryCitizenshipId").enable();
                    this.personalDetailsFormGroup.get("secondaryCitizenshipId").enable();
                    this.personalDetailsFormGroup.get("address").enable();
                    this.personalDetailsFormGroup.get("city").enable();
                    this.personalDetailsFormGroup.get("postCode").enable();
                    this.personalDetailsFormGroup.get("countryId").enable();
                    this.personalDetailsFormGroup.get("phoneNo").enable();

                    this.personalDetailsFormGroup.get("scholasticPeriodId").reset();
                    this.personalDetailsFormGroup.get("additionalScholasticPeriodId").reset();
                }

                const applicationType = _.find(this.applicationTypes, { "id": selectedApplicationType }) as any;

                this.filteredStudyAreas = this.studyAreas.filter(f => f.admissionsApplicationStudyAreasApplicationTypes.find(y => y.admissionsApplicationTypeId === applicationType.id)) as any;

                this.filteredScholasticPeriods = this.allowEdit ? _.filter(this.scholasticPeriods, { "schoolId": applicationType.schoolId }) as any : this.allScholasticPeriods;

                //study abroad
                if (selectedApplicationType === 3) {
                    //this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").setValidators(null);
                    //this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").reset();
                    this.educationFormGroup.get("essayAttachment").setValidators(null);

                    this.currentScholasticPeriod = _.find(this.allScholasticPeriods, { "id": this.personalDetailsFormControls.scholasticPeriodId.value }) as any;

                    //if (this.currentScholasticPeriod && this.currentScholasticPeriod.title.toLowerCase().indexOf("sum") !== -1)
                    //    this.personalDetailsFormGroup.get("isCurrentEngineeringStudent").enable();
                }
                //else {
                //    this.personalDetailsFormGroup.get("isCurrentEngineeringStudent").reset();
                //    this.personalDetailsFormGroup.get("isCurrentEngineeringStudent").disable();
                //}

                //enlus
                if (selectedApplicationType === 4) {
                    this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").setValue(9);
                    this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").disable();

                    //this.educationFormGroup.get("essayAttachment").setValidators(null);
                }

                //graduate
                if (selectedApplicationType === 5) {
                    let markDirty = false;

                    if (this.educationDetailsFormControls.gapYearDetails.value && this.educationDetailsFormControls.gapYearDetails.value.trim()) {
                        this.educationFormGroup.get("gapYearDetails").reset();
                        markDirty = true;;
                    }

                    if (this.educationDetailsFormControls.misconductInfo.value && this.educationDetailsFormControls.misconductInfo.value.trim()) {
                        this.educationFormGroup.get("misconductInfo").reset();
                        markDirty = true;
                    }

                    if (markDirty) {
                        this.educationFormGroup.markAsDirty();
                    }
                }

                if (selectedApplicationType === 1 || selectedApplicationType === 2 || selectedApplicationType === 5 || !this.allowEdit) {
                    this.personalDetailsFormGroup.get("additionalScholasticPeriodId").disable();
                } else {
                    this.personalDetailsFormGroup.get("additionalScholasticPeriodId").enable();
                }

                if (selectedApplicationType !== 4 && this.allowEdit) {
                    this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").setValidators([Validators.required]);
                    this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").reset();
                    this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").enable();
                }

                if (selectedApplicationType !== 3 && this.allowEdit) {
                    if (!this.essayFileName)
                        this.educationFormGroup.get("essayAttachment").setValidators([Validators.required, FileValidator.maxContentSize(this.maxSize)]);
                    else
                        this.educationFormGroup.get("essayAttachment").setValidators([FileValidator.maxContentSize(this.maxSize)]);
                }

                this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").updateValueAndValidity();

                this.educationFormGroup.get("essayAttachment").updateValueAndValidity();
            });

        this.personalDetailsFormGroup.get("scholasticPeriodId").valueChanges
            .subscribe(selectedScholasticPeriodId => {
                this.personalDetailsFormGroup.get("additionalScholasticPeriodId").reset();
                this.reviewFormControls.scholasticPeriodId.setValue(selectedScholasticPeriodId);

                if (this.currentScholasticPeriod) {
                    _.remove(this.existingApplicationScholasticPeriods, (f: any) => {
                        if (f.programId)
                            return f.programId === this.currentScholasticPeriod.programId;
                        else
                            return f.scholasticPeriodId === this.currentScholasticPeriod.scholasticPeriodId;
                    });
                }

                this.currentScholasticPeriod = _.find(this.allScholasticPeriods, { "id": selectedScholasticPeriodId }) as any;

                _.forEach(this.filteredScholasticPeriods, f => {
                    f.isDisabled = f.id === this.currentScholasticPeriod?.id || f.startDate < this.currentScholasticPeriod?.startDate || !f.isOpenForAdmissions;
                });
            });

        this.personalDetailsFormGroup.get("additionalScholasticPeriodId").valueChanges
            .subscribe(selectedScholasticPeriodId => {
                if (!selectedScholasticPeriodId)
                    this.personalDetailsFormGroup.get("additionalScholasticPeriodId").setValue(null, { emitEvent: false });

                this.reviewFormControls.additionalScholasticPeriodId.setValue(selectedScholasticPeriodId);
            });

        this.personalDetailsFormGroup.get("admissionsApplicationStudyAreaId").valueChanges
            .subscribe(selectedStudyAreaId => {
                this.reviewFormControls.admissionsApplicationStudyAreaId.setValue(selectedStudyAreaId);
            });

        this.personalDetailsFormGroup.get("admissionsApplicationSourceId").valueChanges
            .subscribe(selectedSourceId => {
                this.reviewFormControls.admissionsApplicationSourceId.setValue(selectedSourceId);
            });

        this.personalDetailsFormGroup.get("genderTypeId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.genderTypeId.setValue(data);
            });

        this.personalDetailsFormGroup.get("birthStateId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.birthStateId.setValue(data);
            });

        this.personalDetailsFormGroup.get("languageId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.languageId.setValue(data);
            });

        this.personalDetailsFormGroup.get("primaryCitizenshipId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.primaryCitizenshipId.setValue(data);
            });

        this.personalDetailsFormGroup.get("secondaryCitizenshipId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.secondaryCitizenshipId.setValue(data);
            });

        this.personalDetailsFormGroup.get("stateId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.stateId.setValue(data);
            });

        this.familyFormGroup.get("countryId").valueChanges
            .subscribe(selectedCountry => {
                this.reviewFormControls.primaryEmergencyContactCountryId.setValue(selectedCountry);

                var phoneCountry = _.find(this.countries, { "id": selectedCountry }) as ICountry;
                var currentPhoneDialingCode = _.find(this.countries, { "dialingCode": this.familyDetailsFormControls.phoneNo.value }) as ICountry;

                if (phoneCountry && (!this.familyDetailsFormControls.phoneNo.value || currentPhoneDialingCode)) {
                    setTimeout(() => {
                        this.familyFormGroup.get("phoneNo").setValue(phoneCountry.dialingCode);
                    }, null);
                }

                this.filteredEmergencyContact1States = this.states.filter((f: any) => f.countryId === selectedCountry);

                if ((selectedCountry === 1 || selectedCountry === 11) && this.allowEdit) {
                    this.familyFormGroup.get("stateId").enable();
                } else {
                    this.familyFormGroup.get("stateId").reset();
                    this.familyFormGroup.get("stateId").disable();
                }
            });

        this.familyFormGroup.get("countryId2").valueChanges
            .subscribe(selectedCountry => {
                this.reviewFormControls.secondaryEmergencyContactCountryId.setValue(selectedCountry);

                var phoneCountry = _.find(this.countries, { "id": selectedCountry }) as ICountry;
                var currentPhoneDialingCode = _.find(this.countries, { "dialingCode": this.familyDetailsFormControls.phoneNo2.value }) as ICountry;

                if (phoneCountry && (!this.familyDetailsFormControls.phoneNo2.value || currentPhoneDialingCode)) {
                    setTimeout(() => {
                        this.familyFormGroup.get("phoneNo2").setValue(phoneCountry.dialingCode);
                    }, null);
                }

                this.filteredEmergencyContact2States = this.states.filter((f: any) => f.countryId === selectedCountry);

                if ((selectedCountry === 1 || selectedCountry === 11) && this.allowEdit) {
                    this.familyFormGroup.get("stateId2").enable();
                } else {
                    this.familyFormGroup.get("stateId2").reset();
                    this.familyFormGroup.get("stateId2").disable();
                }
            });

        this.familyFormGroup.get("stateId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.primaryEmergencyContactStateId.setValue(data);
            });

        this.familyFormGroup.get("stateId2").valueChanges
            .subscribe(data => {
                this.reviewFormControls.secondaryEmergencyContactStateId.setValue(data);
            });

        this.familyFormGroup.get("birthCountryId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.primaryEmergencyContactBirthCountryId.setValue(data);
            });

        this.familyFormGroup.get("birthCountryId2").valueChanges
            .subscribe(data => {
                this.reviewFormControls.secondaryEmergencyContactBirthCountryId.setValue(data);
            });

        this.familyFormGroup.get("genderTypeId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.primaryEmergencyContactGenderTypeId.setValue(data);
            });

        this.familyFormGroup.get("genderTypeId2").valueChanges
            .subscribe(data => {
                this.reviewFormControls.secondaryEmergencyContactGenderTypeId.setValue(data);
            });

        this.familyFormGroup.get("educationLevelId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.educationLevelId.setValue(data);
            });

        this.familyFormGroup.get("educationLevelId2").valueChanges
            .subscribe(data => {
                this.reviewFormControls.educationLevelId2.setValue(data);
            });

        this.familyFormGroup.get("studentEmergencyContactTypeId").valueChanges
            .subscribe(data => {
                this.reviewFormControls.studentEmergencyContactTypeId.setValue(data);
            });

        this.familyFormGroup.get("studentEmergencyContactTypeId2").valueChanges
            .subscribe(data => {
                this.reviewFormControls.studentEmergencyContactTypeId2.setValue(data);
            });

        this.familyFormGroup.get("firstName2").valueChanges
            .subscribe(firstName => {
                if (firstName && this.allowEdit) {
                    this.familyFormGroup.get("lastName2").enable();
                    this.familyFormGroup.get("genderTypeId2").enable();
                    this.familyFormGroup.get("studentEmergencyContactTypeId2").enable();
                    this.familyFormGroup.get("birthCountryId2").enable();
                    this.familyFormGroup.get("address2").enable();
                    this.familyFormGroup.get("city2").enable();
                    this.familyFormGroup.get("postCode2").enable();
                    this.familyFormGroup.get("countryId2").enable();
                    this.familyFormGroup.get("email2").enable();
                    this.familyFormGroup.get("phoneNo2").enable();
                    this.familyFormGroup.get("educationLevelId2").enable();
                    this.familyFormGroup.get("occupation2").enable();
                    this.familyFormGroup.get("employer2").enable();
                    this.familyFormGroup.get("isEmergencyContact2").enable();
                } else {
                    this.familyFormGroup.get("lastName2").disable();
                    this.familyFormGroup.get("genderTypeId2").disable();
                    this.familyFormGroup.get("studentEmergencyContactTypeId2").disable();
                    this.familyFormGroup.get("birthCountryId2").disable();
                    this.familyFormGroup.get("address2").disable();
                    this.familyFormGroup.get("city2").disable();
                    this.familyFormGroup.get("countryId2").disable();
                    this.familyFormGroup.get("postCode2").disable();
                    this.familyFormGroup.get("email2").disable();
                    this.familyFormGroup.get("phoneNo2").disable();
                    this.familyFormGroup.get("educationLevelId2").disable();
                    this.familyFormGroup.get("occupation2").disable();
                    this.familyFormGroup.get("employer2").disable();
                    this.familyFormGroup.get("isEmergencyContact2").disable();
                }

                if (!firstName && this.allowEdit) {
                    this.familyFormGroup.get("lastName2").reset();
                    this.familyFormGroup.get("genderTypeId2").reset();
                    this.familyFormGroup.get("studentEmergencyContactTypeId2").reset();
                    this.familyFormGroup.get("birthCountryId2").reset();
                    this.familyFormGroup.get("address2").reset();
                    this.familyFormGroup.get("city2").reset();
                    this.familyFormGroup.get("postCode2").reset();
                    this.familyFormGroup.get("countryId2").reset();
                    this.familyFormGroup.get("email2").reset();
                    this.familyFormGroup.get("phoneNo2").reset();
                    this.familyFormGroup.get("educationLevelId2").reset();
                    this.familyFormGroup.get("occupation2").reset();
                    this.familyFormGroup.get("employer2").reset();
                    this.familyFormGroup.get("isEmergencyContact2").reset();
                }
            });

        this.filteredProviders = this.educationFormGroup.get("organizationBbUniqueId").valueChanges
            .pipe(
                debounceTime(1000),
                (startWith<string | IProvider>("")) as any,
                switchMap(val => {
                    return this.filterProvider(val as string || "");
                })
            );

        this.educationFormGroup.get("organizationBbUniqueId").valueChanges
            .subscribe(organizationBbUniqueId => {
                if (organizationBbUniqueId && organizationBbUniqueId !== "") {
                    const provider = _.find(this.providers, { "organizationBbUniqueId": organizationBbUniqueId }) as any;

                    if (provider) {
                        setTimeout(() => {
                            this.educationFormGroup.get("city").setValue(provider.city);
                            this.educationFormGroup.get("countryId").setValue(provider.countryId);
                            this.educationFormGroup.get("stateId").setValue(provider.stateId);
                        }, null);

                        if (provider.city)
                            this.educationFormGroup.get("city").disable();
                        else
                            this.educationFormGroup.get("city").enable();

                        if (provider.countryId)
                            this.educationFormGroup.get("countryId").disable();
                        else
                            this.educationFormGroup.get("countryId").enable();

                        if (provider.stateId)
                            this.educationFormGroup.get("stateId").disable();
                        else
                            this.educationFormGroup.get("stateId").enable();
                    } else {
                        setTimeout(() => {
                            this.educationFormGroup.get("city").setValue(null);
                            this.educationFormGroup.get("countryId").setValue(null);
                        }, null);

                        this.educationFormGroup.get("city").enable();
                        this.educationFormGroup.get("countryId").enable();
                    }
                }
            });

        this.educationFormGroup.get("countryId").valueChanges
            .subscribe(selectedCountry => {
                const provider = _.find(this.providers, { "organizationBbUniqueId": this.educationFormGroup.get("organizationBbUniqueId").value }) as any;

                if (selectedCountry === 1 && this.allowEdit && !provider) {
                    this.educationFormGroup.get("stateId").enable();
                } else {
                    this.educationFormGroup.get("stateId").reset();
                    this.educationFormGroup.get("stateId").disable();
                }
            });
    }

    private filterProvider(name: string): Observable<IProvider[]> {
        if (name.length > 0) {
            const filterValue = name.toLowerCase();

            return (this.lookupsService.getBbOrganizations(filterValue)
                .pipe(
                    (map((response: IProvider[]) => {
                        this.providers = response;

                        return response;
                    })) as any
                )) as any;
        }

        return new Observable<IProvider[]>();
    }

    clearProvider(resetAll: boolean) {
        if (resetAll) {
            this.educationFormGroup.get("organizationBbUniqueId").setValue(null);

            this.educationFormGroup.get("city").setValue(null);
            this.educationFormGroup.get("countryId").setValue(null);
            this.educationFormGroup.get("stateId").setValue(null);

            this.educationFormGroup.get("city").enable();
            this.educationFormGroup.get("countryId").enable();
        }

        //this.providers = [];
        this.matAutocomplete.options.reset([]);
    }

    displayProvider(organizationBbUniqueId: number): string | undefined {
        if (!organizationBbUniqueId) return undefined;

        const provider = _.find(this.providers, { "organizationBbUniqueId": organizationBbUniqueId });

        if (provider)
            return provider.name;

        return undefined;
    }

    chooseFirstProvider(): void {
        if (this.matAutocomplete.options.first && (this.matAutocomplete.options.first.value === this.educationFormGroup.get("organizationBbUniqueId").value || typeof this.educationFormGroup.get("organizationBbUniqueId").value === "string")) {
            this.matAutocomplete.options.first.select();

            this.educationFormGroup.get("organizationBbUniqueId").setValue(this.matAutocomplete.options.first.value);
        }
    }

    get personalDetailsFormControls() { return this.personalDetailsFormGroup.controls as any; }

    get educationDetailsFormControls() { return this.educationFormGroup.controls as any; }

    get familyDetailsFormControls() { return this.familyFormGroup.controls as any; }

    get reviewFormControls() { return this.reviewFormGroup.controls as any; }

    savePersonalDetails(stepper: MatStepper, submitBtn: HTMLButtonElement) {
        // stop here if form is invalid
        if (this.personalDetailsFormGroup.invalid) {
            return;
        }

        //if there where any changes...update the backend
        if (this.personalDetailsFormGroup.dirty) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const admissionsApplicationPersonalInformationModel = {
                firstName: this.personalDetailsFormControls.firstName.value,
                lastName: this.personalDetailsFormControls.lastName.value,
                middleName: !this.personalDetailsFormControls.middleName.value || !this.personalDetailsFormControls.middleName.value.trim() ? null : this.personalDetailsFormControls.middleName.value.trim(),
                //isCurrentEngineeringStudent: this.personalDetailsFormControls.isCurrentEngineeringStudent.value
                //    ? this.personalDetailsFormControls.isCurrentEngineeringStudent.value
                //    : false,
                genderTypeId: this.personalDetailsFormControls.genderTypeId.value,
                dateOfBirth: moment(this.personalDetailsFormControls.dateOfBirth.value).format("YYYY-MM-DD"),
                birthCity: this.personalDetailsFormControls.birthCity.value,
                birthStateId: this.personalDetailsFormControls.birthStateId.value,
                birthCountryId: this.personalDetailsFormControls.birthCountryId.value,
                languageId: this.personalDetailsFormControls.languageId.value,
                primaryCitizenshipId: this.personalDetailsFormControls.primaryCitizenshipId.value,
                secondaryCitizenshipId: this.personalDetailsFormControls.secondaryCitizenshipId.value,
                address: this.personalDetailsFormControls.address.value,
                city: this.personalDetailsFormControls.city.value,
                stateId: this.personalDetailsFormControls.stateId.value,
                postCode: this.personalDetailsFormControls.postCode.value,
                countryId: this.personalDetailsFormControls.countryId.value,
                phoneNo: this.personalDetailsFormControls.phoneNo.value,
                email: this.personalDetailsFormControls.email.value,
            };

            const scholasticPeriod = this.filteredScholasticPeriods.find(f => f.id === this.personalDetailsFormControls.scholasticPeriodId.value);
            const additionalScholasticPeriod = this.filteredScholasticPeriods.find(f => f.id === this.personalDetailsFormControls.additionalScholasticPeriodId.value);

            const saveModel = {
                id: this.applicationId,
                studentRegistrationId: this.currentUser.id,
                admissionsApplicationStatusId: 0,
                admissionsApplicationRegistrationStatusId: 0,
                scholasticPeriodId: scholasticPeriod.scholasticPeriodId ? scholasticPeriod.scholasticPeriodId : scholasticPeriod.programScholasticPeriodId,
                additionalScholasticPeriodId: additionalScholasticPeriod ? (additionalScholasticPeriod.scholasticPeriodId ? additionalScholasticPeriod.scholasticPeriodId : additionalScholasticPeriod.programScholasticPeriodId) : null,
                programId: scholasticPeriod.programId,
                additionalProgramId: additionalScholasticPeriod ? additionalScholasticPeriod.programId : null,
                admissionsApplicationStepId: 0,
                admissionsApplicationTypeId: this.personalDetailsFormControls.admissionsApplicationTypeId.value,
                schoolId: this.applicationTypes.find(f => f.id === this.personalDetailsFormControls.admissionsApplicationTypeId.value).schoolId,
                admissionsApplicationOriginId: 0,
                admissionsApplicationSourceId: this.personalDetailsFormControls.admissionsApplicationSourceId.value,
                admissionsApplicationStudyAreaId: this.personalDetailsFormControls.admissionsApplicationStudyAreaId.value,
                waiverCode: !this.personalDetailsFormControls.waiverCode.value || !this.personalDetailsFormControls.waiverCode.value.trim() ? null : this.personalDetailsFormControls.waiverCode.value.trim(),
                admissionsApplicationPersonalInformation: admissionsApplicationPersonalInformationModel,
                rowversion: this.applicationRowversion
            };

            this.admissionsService.insertOrUpdateAdmissionsApplicationPersonalDetails(saveModel)
                .pipe(first())
                .subscribe((result: any) => {
                    this.currentUser.firstName = result.admissionsApplicationPersonalInformation.firstName;
                    this.currentUser.lastName = result.admissionsApplicationPersonalInformation.lastName;
                    this.applicationId = result.id;
                    this.applicationRowversion = result.rowversion;

                    if (result.admissionsApplicationEducationDetail && !result.admissionsApplicationEducationDetail.essayAttachmentId) {
                        this.essayFileName = null;
                        this.essayFileType = null;
                        this.essayAttachmentId = null;

                        //update since the attachment might have been deleted
                        this.educationDetailsRowversion = result.admissionsApplicationEducationDetail.rowversion;
                    }

                    //remove any notifications
                    this.currentUser.hasAdmissionsApplication = true;
                    _.remove(this.currentUser.userNotifications, f => f.code === "admissionsApplication");

                    this.storageService.store(this.appGlobals.localStorageKeys.currentUser, this.currentUser);

                    this.personalDetailsFormGroup.markAsPristine();

                    stepper.next();
                });
        } else {
            stepper.next();
        }
    }

    saveEducationDetails(stepper: MatStepper, submitBtn: HTMLButtonElement) {
        // stop here if form is invalid
        if (this.educationFormGroup.invalid || !this.educationCustomValidation()) {
            if (this.educationDetailsFormControls.essayAttachment.invalid) {
                this.educationDetailsFormControls.essayAttachment.markAsTouched({ onlySelf: true });
            }

            return;
        }

        //if there where any changes...update the backend
        if (this.educationFormGroup.dirty || this.educationInformationChanged) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const admissionsApplicationEducationInformationList = [];

            _.forEach(this.educationDetails.value, (f: any) => {
                const admissionsApplicationEducationInformation = {
                    id: f.guid ? 0 : f.id,
                    admissionsApplicationEducationDetailsId: this.educationDetailsId,
                    organizationBbUniqueId: f.organizationBbUniqueId,
                    school: f.organizationBbUniqueId === null ? f.schoolDescription.trim() : null,
                    countryId: f.countryId,
                    stateId: f.stateId,
                    city: f.city,
                    enrollmentDateFrom: f.enrollmentDateFrom,
                    enrollmentDateTo: f.enrollmentDateTo
                }

                admissionsApplicationEducationInformationList.push((admissionsApplicationEducationInformation) as any);
            });

            const educationDetailsModel = {
                id: this.educationDetailsId,
                admissionsApplicationId: this.applicationId,
                gapYearDetails: !this.educationDetailsFormControls.gapYearDetails.value || !this.educationDetailsFormControls.gapYearDetails.value.trim() ? null : this.educationDetailsFormControls.gapYearDetails.value.trim(),
                misconductInfo: !this.educationDetailsFormControls.misconductInfo.value || !this.educationDetailsFormControls.misconductInfo.value.trim() ? null : this.educationDetailsFormControls.misconductInfo.value.trim(),
                fileName: this.educationDetailsFormControls.essayAttachment.value ? this.educationDetailsFormControls.essayAttachment.value.files[0].name : this.essayFileName,
                fileType: this.educationDetailsFormControls.essayAttachment.value ? this.educationDetailsFormControls.essayAttachment.value.files[0].type : this.essayFileType,
                essayAttachmentId: this.essayAttachmentId,
                admissionsApplicationEducationInformations: admissionsApplicationEducationInformationList,
                rowversion: this.educationDetailsRowversion,
                applicationRowversion: this.applicationRowversion
            };

            const saveModel = new FormData();

            saveModel.append("educationDetailsModel", JSON.stringify(educationDetailsModel));

            if (this.educationDetailsFormControls.essayAttachment.value)
                saveModel.append("essayAttachment", this.educationDetailsFormControls.essayAttachment.value.files[0], this.educationDetailsFormControls.essayAttachment.value.files[0].name);

            this.admissionsService.insertOrUpdateAdmissionsApplicationEducationDetails(saveModel)
                .pipe(first())
                .subscribe((result: any) => {
                    this.applicationRowversion = result.applicationRowversion;
                    this.educationDetailsRowversion = result.rowversion;
                    this.educationDetailsId = result.id;
                    this.essayFileName = result.fileName;
                    this.essayFileType = result.fileType;
                    this.essayAttachmentId = result.essayAttachmentId;

                    if (this.essayAttachmentInput) {
                        this.essayAttachmentInput.clear();
                        this.educationDetailsFormControls.essayAttachment.markAsPristine();
                        this.educationDetailsFormControls.essayAttachment.markAsUntouched();
                        this.educationDetailsFormControls.essayAttachment.setValidators([FileValidator.maxContentSize(this.maxSize)]);
                        this.educationDetailsFormControls.essayAttachment.updateValueAndValidity();
                    }

                    //this.educationDetails.next(null);
                    _.forEach(result.admissionsApplicationEducationInformations, (f: any) => {
                        const country = _.find(this.countries, { "id": f.countryId }) as ICountry;
                        f.countryDescription = country.description;

                        const state = _.find(this.states, { "id": f.stateId }) as any;
                        f.stateDescription = state ? state.description : null;

                        if (f.school) {
                            f.schoolDescription = f.school;
                        } else {
                            //const provider = _.find(this.providers, { "organizationBbUniqueId": f.organizationBbUniqueId }) as IProvider;
                            f.schoolDescription = f.organizationName;
                        }
                    });

                    this.educationDetails.next(_.orderBy(result.admissionsApplicationEducationInformations, ["enrollmentDateFrom"], ["desc"]));

                    this.educationFormGroup.markAsPristine();
                    this.educationInformationChanged = false;

                    stepper.next();
                });
        } else
            stepper.next();
    }

    saveFamilyDetails(stepper: MatStepper, submitBtn: HTMLButtonElement) {
        // stop here if form is invalid
        if (this.familyFormGroup.invalid) {
            return;
        }

        //if there where any changes...update the backend
        if (this.familyFormGroup.dirty || this.existingEmergencyContactInformationFound) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const admissionsApplicationFamilyInformationList = [];

            const admissionsApplicationFamilyInformation = {
                id: this.primaryContactId,
                AdmissionsApplicationId: this.applicationId,
                firstName: this.familyDetailsFormControls.firstName.value,
                lastName: this.familyDetailsFormControls.lastName.value,
                genderTypeId: this.familyDetailsFormControls.genderTypeId.value,
                studentEmergencyContactTypeId: this.familyDetailsFormControls.studentEmergencyContactTypeId.value,
                birthCountryId: this.familyDetailsFormControls.birthCountryId.value,
                address: !this.familyDetailsFormControls.address.value || !this.familyDetailsFormControls.address.value.trim() ? null : this.familyDetailsFormControls.address.value.trim(),
                city: !this.familyDetailsFormControls.city.value || !this.familyDetailsFormControls.city.value.trim() ? null : this.familyDetailsFormControls.city.value.trim(),
                stateId: this.familyDetailsFormControls.stateId.value,
                postCode: !this.familyDetailsFormControls.postCode.value || !this.familyDetailsFormControls.postCode.value.trim() ? null : this.familyDetailsFormControls.postCode.value.trim(),
                countryId: this.familyDetailsFormControls.countryId.value,
                phoneNo: this.familyDetailsFormControls.phoneNo.value,
                email: !this.familyDetailsFormControls.email.value || !this.familyDetailsFormControls.email.value.trim() ? null : this.familyDetailsFormControls.email.value.trim(),
                educationLevelId: this.familyDetailsFormControls.educationLevelId.value,
                occupation: !this.familyDetailsFormControls.occupation.value || !this.familyDetailsFormControls.occupation.value.trim() ? null : this.familyDetailsFormControls.occupation.value.trim(),
                employer: !this.familyDetailsFormControls.employer.value || !this.familyDetailsFormControls.employer.value.trim() ? null : this.familyDetailsFormControls.employer.value.trim(),
                isEmergencyContact: this.familyDetailsFormControls.isEmergencyContact.value ? this.familyDetailsFormControls.isEmergencyContact.value : false,
                isPrimaryContact: true,
                rowversion: this.primaryContactRowversion
            }

            admissionsApplicationFamilyInformationList.push((admissionsApplicationFamilyInformation) as any);

            if (this.familyDetailsFormControls.firstName2.value) {
                const admissionsApplicationFamilyInformation2 = {
                    id: this.secondaryContactId,
                    AdmissionsApplicationId: this.applicationId,
                    firstName: this.familyDetailsFormControls.firstName2.value,
                    lastName: this.familyDetailsFormControls.lastName2.value,
                    genderTypeId: this.familyDetailsFormControls.genderTypeId2.value,
                    studentEmergencyContactTypeId: this.familyDetailsFormControls.studentEmergencyContactTypeId2.value,
                    birthCountryId: this.familyDetailsFormControls.birthCountryId2.value,
                    address: !this.familyDetailsFormControls.address2.value || !this.familyDetailsFormControls.address2.value.trim() ? null : this.familyDetailsFormControls.address2.value.trim(),
                    city: !this.familyDetailsFormControls.city2.value || !this.familyDetailsFormControls.city2.value.trim() ? null : this.familyDetailsFormControls.city2.value.trim(),
                    stateId: this.familyDetailsFormControls.stateId2.value,
                    postCode: !this.familyDetailsFormControls.postCode2.value || !this.familyDetailsFormControls.postCode2.value.trim() ? null : this.familyDetailsFormControls.postCode2.value.trim(),
                    countryId: this.familyDetailsFormControls.countryId2.value,
                    phoneNo: this.familyDetailsFormControls.phoneNo2.value,
                    email: !this.familyDetailsFormControls.email2.value || !this.familyDetailsFormControls.email2.value.trim() ? null : this.familyDetailsFormControls.email2.value.trim(),
                    educationLevelId: this.familyDetailsFormControls.educationLevelId2.value,
                    occupation: !this.familyDetailsFormControls.occupation2.value || !this.familyDetailsFormControls.occupation2.value.trim() ? null : this.familyDetailsFormControls.occupation2.value.trim(),
                    employer: !this.familyDetailsFormControls.employer2.value || !this.familyDetailsFormControls.employer2.value.trim() ? null : this.familyDetailsFormControls.employer2.value.trim(),
                    isEmergencyContact: this.familyDetailsFormControls.isEmergencyContact2.value ? this.familyDetailsFormControls.isEmergencyContact2.value : false,
                    isPrimaryContact: false,
                    rowversion: this.secondaryContactRowversion
                }

                admissionsApplicationFamilyInformationList.push((admissionsApplicationFamilyInformation2) as any);
            }

            const saveModel = {
                admissionsApplicationId: this.applicationId,
                applicationRowVersion: this.applicationRowversion,
                admissionsApplicationFamilyInformations: admissionsApplicationFamilyInformationList
            };

            this.admissionsService.insertOrUpdateAdmissionsApplicationFamilyDetails(saveModel)
                .pipe(first())
                .subscribe((result: any) => {
                    this.applicationRowversion = result.applicationRowversion;

                    const primaryContact = _.find(result.admissionsApplicationFamilyInformations, ["isPrimaryContact", true]) as any;

                    this.primaryContactRowversion = primaryContact.rowversion;
                    this.primaryContactId = primaryContact.id;

                    const secondaryContact = _.find(result.admissionsApplicationFamilyInformations, ["isPrimaryContact", false]) as any;

                    if (secondaryContact) {
                        this.secondaryContactRowversion = secondaryContact.rowversion;
                        this.secondaryContactId = secondaryContact.id;
                    }

                    this.familyFormGroup.markAsPristine();

                    stepper.next();
                });
        } else
            stepper.next();
    }

    saveReviewDetails(submitBtn: HTMLButtonElement) {
        // stop here if form is invalid
        if (this.reviewFormGroup.invalid) {
            return;
        }

        if (this.reviewFormGroup.dirty) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            const admissionsApplicationType = this.applicationTypes.find(f => f.id === this.personalDetailsFormControls.admissionsApplicationTypeId.value).description;

            ////We call the event emitter function from our service and pass in the details
            //this.googleAnalyticsService.eventEmitter("Admissions", "Admissions Application Submitted", `${admissionsApplicationType} Appplication`, 1);

            this.googleAnalyticsService.logEvent("admissions_form_submit","Admissions","Admissions Application Submitted", `${admissionsApplicationType} Appplication`);

            const saveModel = {
                applicationId: this.applicationId,
                isHousingRequired: this.reviewFormControls.isHousingRequired.value,
                rowversion: this.applicationRowversion
            }

            this.admissionsService.submitApplication(saveModel)
                .pipe(first())
                .subscribe(() => {
                    this.alertService.showMessage("Application Successfully Submitted.", "Admissions Application", this.appGlobals.alertMessageTypes.success);

                    this.dialogRef.close();
                });
        }
    }

    close() {
        if (this.personalDetailsFormGroup.dirty || this.educationFormGroup.dirty || this.educationInformationChanged || this.familyFormGroup.dirty || this.reviewFormGroup.dirty) {
            //show unsaved changes dialog
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = this.isMobile ? "60vw" : "30vw";
            dialogConfig.height = "auto";
            dialogConfig.panelClass = "unSavedChanges-dialog-container";

            const dialogRef = this.dialog.open(UnSavedChangesDialogComponent, dialogConfig);

            dialogRef.afterClosed().subscribe(result => {
                if (result === "confirm") {
                    this.dialogRef.close();
                }
            });
        } else {
            this.dialogRef.close();
        }
    }

    addEducationDetails() {
        const organizationBbUniqueId = this.educationDetailsFormControls.organizationBbUniqueId.value;
        const countryId = this.educationDetailsFormControls.countryId.value;
        const stateId = this.educationDetailsFormControls.stateId.value;
        const city = this.educationDetailsFormControls.city.value;
        const enrollmentDateFrom = this.educationDetailsFormControls.enrollmentDateFrom.value;
        const enrollmentDateTo = this.educationDetailsFormControls.enrollmentDateTo.value;

        const school = _.find(this.providers, { "organizationBbUniqueId": organizationBbUniqueId }) as IProvider;
        const country = _.find(this.countries, { "id": countryId }) as ICountry;
        const state = _.find(this.states, { "id": stateId }) as any;

        //do not allow duplicate education entries
        //const result = this.groupBy(this.educationDetails.value, item => [item.enrollmentDateFrom, item.enrollmentDateTo, item.schoolDescription]);

        if (organizationBbUniqueId && countryId && city && enrollmentDateFrom && enrollmentDateTo && ((countryId === 1 && stateId) || countryId !== 1) && !this.educationDetailsFormControls.enrollmentDateTo.invalid) {
            if (this.educationDetails.value.find(f => f.enrollmentDateFrom === moment(enrollmentDateFrom).format("YYYY-MM-DD") && f.enrollmentDateTo === moment(enrollmentDateTo).format("YYYY-MM-DD") && f.schoolDescription === (typeof organizationBbUniqueId === "string" ? organizationBbUniqueId : school.name))) {
                this.alertService.showMessage("Duplicate Education Entries are Not Allowed", "Admissions Application", this.appGlobals.alertMessageTypes.error);
            } else {
                const index = this.educationDetails.value.length === 0 ? 0 : _.maxBy(this.educationDetails.value, "id").id + 1;

                this.educationDetails$
                    .pipe(take(1))
                    .subscribe(val => {
                        const educationData = [...val, {
                            id: index,
                            organizationBbUniqueId: typeof organizationBbUniqueId === "string" ? null : organizationBbUniqueId,
                            schoolDescription: typeof organizationBbUniqueId === "string" ? organizationBbUniqueId : school.name,
                            countryId: countryId,
                            countryDescription: country.description,
                            stateId: stateId,
                            stateDescription: state ? state.description : null,
                            city: city,
                            enrollmentDateFrom: moment(enrollmentDateFrom).format("YYYY-MM-DD"),
                            enrollmentDateTo: moment(enrollmentDateTo).format("YYYY-MM-DD"),
                            isResyncEnabled: true,
                            guid: Guid.create()
                        }];

                        this.educationDetails.next(_.orderBy(educationData, ["enrollmentDateFrom"], ["desc"]));
                        //this.educationDetails.next(_.orderBy(educationData, f => {
                        //    if (typeof f.enrollmentDate == "string")
                        //}));

                        this.educationDetailsFormControls.organizationBbUniqueId.reset();
                        this.educationDetailsFormControls.countryId.reset();
                        this.educationDetailsFormControls.stateId.reset();
                        this.educationDetailsFormControls.city.reset();
                        this.educationDetailsFormControls.enrollmentDateFrom.reset();
                        this.educationDetailsFormControls.enrollmentDateTo.reset();

                        this.educationInformationChanged = true;
                    });
            }
        }
    }

    deleteEducationData(id: number) {
        this.educationDetails$
            .pipe(take(1))
            .subscribe(val => {
                const educationData = [...val];

                var index = educationData.findIndex(((elt) => elt.id === id) as any);
                educationData.splice(index, 1);

                this.educationDetails.next(_.orderBy(educationData, ["enrollmentDateFrom"], ["desc"]));

                this.educationInformationChanged = true;
            });
    }

    educationCustomValidation() {
        if (this.educationDetailsFormControls.essayAttachment.value && _.find(this.educationDetailsFormControls.essayAttachment.value.files, (f: File) => f.type !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document" && f.type !== "application/pdf")) {
            this.alertService.showMessage("Only pdf or word documents are allowed.", "Admissions Application", this.appGlobals.alertMessageTypes.error);

            return false;
        }

        if (this.allowEdit && this.educationDetails.value.length === 0) {
            this.alertService.showMessage("Education Details are required", "Admissions Application", this.appGlobals.alertMessageTypes.error);

            return false;
        }
        return true;
    }

    handleFileInput(files: FileList) {
        this.fileToUpload = files.item(0);
    }

    openDocument(essayAttachmentId: number) {
        this.userService.openAttachment(essayAttachmentId)
            .pipe(first())
            .subscribe((response: any) => {
                const file = new Blob([response], { type: this.essayFileType });
                var fileUrl = URL.createObjectURL(file);
                window.open(fileUrl);
            });
    }

    existingScholasticPeriod(scholasticPeriod: any) {
        let program = null;

        if (scholasticPeriod.programId)
            program = this.programs.find(f => f.id === scholasticPeriod.programId);

        return !!this.existingApplicationScholasticPeriods.find((f: any) => scholasticPeriod.programId ? f.programId === scholasticPeriod.programId : f.scholasticPeriodId === scholasticPeriod.scholasticPeriodId) || (program && _.map(program.programAdmissionsApplicationTypes, "admissionsApplicationTypeId").indexOf(this.personalDetailsFormGroup.get("admissionsApplicationTypeId").value) === -1);
    }
}